import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, CalendarRange } from 'lucide-react';

export interface AvailabilityWindow {
  start_time: string; // ISO
  end_time: string; // ISO
}

interface AvailabilityEditorProps {
  value: AvailabilityWindow[];
  onChange: (windows: AvailabilityWindow[]) => void;
}

// Local-time helpers. We construct windows in the browser's local timezone,
// which matches how an organiser thinks about "which day" someone is available.
const pad = (n: number) => String(n).padStart(2, '0');

const toDateStr = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const toTimeStr = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localIso = (date: string, time: string): string => new Date(`${date}T${time}`).toISOString();
const nextDayIso = (date: string): string => {
  const d = new Date(`${date}T00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
};

interface Row {
  date: string;
  allDay: boolean;
  start: string;
  end: string;
}

const windowToRow = (w: AvailabilityWindow): Row => {
  const date = toDateStr(w.start_time);
  const startT = toTimeStr(w.start_time);
  const endT = toTimeStr(w.end_time);
  // Treat 00:00 → next-day 00:00 (rendered as 00:00) as an all-day window.
  const spansFullDay = startT === '00:00' && toDateStr(w.end_time) !== date && endT === '00:00';
  return { date, allDay: spansFullDay, start: startT, end: endT === '00:00' ? '23:59' : endT };
};

const rowToWindow = (row: Row): AvailabilityWindow | null => {
  if (!row.date) return null;
  if (row.allDay) {
    return { start_time: localIso(row.date, '00:00'), end_time: nextDayIso(row.date) };
  }
  if (!row.start || !row.end) return null;
  return { start_time: localIso(row.date, row.start), end_time: localIso(row.date, row.end) };
};

const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Inclusive list of YYYY-MM-DD dates from `from` to `to` (capped for safety).
const datesInRange = (from: string, to: string): string[] => {
  const start = new Date(`${from}T00:00`);
  const end = new Date(`${to}T00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const out: string[] = [];
  const cur = new Date(start);
  while (cur <= end && out.length < 366) {
    out.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

export function AvailabilityEditor({ value, onChange }: AvailabilityEditorProps) {
  const rows = value.map(windowToRow);

  const [bulk, setBulk] = useState<{ from: string; to: string; allDay: boolean; start: string; end: string }>({
    from: todayStr(),
    to: todayStr(),
    allDay: true,
    start: '09:00',
    end: '17:00',
  });

  const commit = (nextRows: Row[]) => {
    onChange(nextRows.map(rowToWindow).filter((w): w is AvailabilityWindow => w !== null));
  };

  const addRange = () => {
    const dates = datesInRange(bulk.from, bulk.to);
    if (dates.length === 0) return;
    const newRows: Row[] = dates.map((date) => ({
      date,
      allDay: bulk.allDay,
      start: bulk.start,
      end: bulk.end,
    }));
    // Append, skipping dates that already have an identical window.
    const existingKeys = new Set(
      rows.map((r) => `${r.date}|${r.allDay ? 'all' : `${r.start}-${r.end}`}`),
    );
    const toAdd = newRows.filter(
      (r) => !existingKeys.has(`${r.date}|${r.allDay ? 'all' : `${r.start}-${r.end}`}`),
    );
    commit([...rows, ...toAdd]);
  };

  const updateRow = (index: number, patch: Partial<Row>) => {
    commit(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    commit([...rows, { date: todayStr(), allDay: true, start: '09:00', end: '17:00' }]);
  };

  const removeRow = (index: number) => {
    commit(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {/* Bulk add a consecutive date range */}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-input bg-muted/20 p-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <input
            type="date"
            value={bulk.from}
            onChange={(e) => setBulk((b) => ({ ...b, from: e.target.value }))}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <input
            type="date"
            value={bulk.to}
            onChange={(e) => setBulk((b) => ({ ...b, to: e.target.value }))}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 pb-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={bulk.allDay}
            onChange={(e) => setBulk((b) => ({ ...b, allDay: e.target.checked }))}
          />
          All day
        </label>
        {!bulk.allDay && (
          <div className="flex items-end gap-1">
            <input
              type="time"
              value={bulk.start}
              onChange={(e) => setBulk((b) => ({ ...b, start: e.target.value }))}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
            <span className="pb-2 text-muted-foreground">–</span>
            <input
              type="time"
              value={bulk.end}
              onChange={(e) => setBulk((b) => ({ ...b, end: e.target.value }))}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
          </div>
        )}
        <Button type="button" size="sm" variant="outline" onClick={addRange}>
          <CalendarRange className="mr-2 h-4 w-4" />
          Add range
        </Button>
      </div>

      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No availability set.</p>
      )}
      {rows.map((row, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={row.date}
            onChange={(e) => updateRow(index, { date: e.target.value })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
          <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={row.allDay}
              onChange={(e) => updateRow(index, { allDay: e.target.checked })}
            />
            All day
          </label>
          {!row.allDay && (
            <>
              <input
                type="time"
                value={row.start}
                onChange={(e) => updateRow(index, { start: e.target.value })}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
              <span className="text-muted-foreground">–</span>
              <input
                type="time"
                value={row.end}
                onChange={(e) => updateRow(index, { end: e.target.value })}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
            </>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => removeRow(index)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={addRow}>
        <Plus className="mr-2 h-4 w-4" />
        Add availability
      </Button>
    </div>
  );
}
